import React, { useState } from 'react'

// styles
import Styles from './verificationForm.module.css'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, injectIntl } from 'react-intl'
import { setSnackbarOpen, setSnackbarData } from '../../snackbar/redux/snackbarSlice'
import { _sendCode, _verify, _reSendCode, _initializeKyc, _addInfo } from '../redux/actions'
// form validations
import * as Yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// components
import FormRow from '../../../components/FormRow/FormRow'
import Input from '../../../components/Input/Input'
import CheckboxInput from '../../../components/CheckboxInput/CheckboxInput'
import Button from '../../../components/Button/Button'
import RequiredIndicator from '../../../components/RequiredIndicator/RequiredIndicator'
import { Link } from 'react-router-dom'

// schema
const formSchema = Yup.object().shape({
  industry: Yup.string().required('Required'),
  accountPurpose: Yup.string().required('Required'),
  expectedMonthlyVolume: Yup.string().required('Required'),
  sourceOfFunds: Yup.string().required('Required'),
  acceptTerms: Yup.boolean()
    .oneOf([true], 'You must agree to the terms and conditions')
    .required('You must agree to the terms and conditions'),
  politicalPerson: Yup.string().required('Required'),
  privacy: Yup.boolean()
    .oneOf([true], 'You must agree to the privacy and policy')
    .required('You must agree to the privacy and policy'),
  risks: Yup.boolean()
    .oneOf([true], 'You must understand the risks of Cryptocurrency')
    .required('You must understand the risks of Cryptocurrency')
})

function VerificationForm({ setStep }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit'
  })

  // hooks
  const dispatch = useDispatch()

  // states expectedMonthlyVolume industry
  const [sourceOfFunds, setSourceOfFunds] = useState(null)
  const [accountPurpose, setAccountPurpose] = useState(null)
  const [industry, setIndustry] = useState(null)
  const [expectedMonthlyVolume, setExpectedMonthlyVolume] = useState(null)
  const [politicalPerson, setPoliticalPerson] = useState(null)
  const [loading, setLoading] = useState(false)
  const theme = useSelector(state => state.theme.value)

  const onSubmit = formData => {
    setLoading(true)
    // console.log(formData)
    _addInfo(
      {
        industry: formData.industry,
        account_opening_purpose: formData.accountPurpose,
        expected_monthly_volume: formData.expectedMonthlyVolume,
        source_of_funds: formData.sourceOfFunds,
        politically_exposed_person: formData.politicalPerson === 'yes'
      },
      data => {
        setStep(4)
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: 'Information Added Successfully',
            severity: 'success',
            callback: () => {},
            position: 'topRight'
          })
        )
        setLoading(false)
      },
      error => {
        setLoading(false)
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
            severity: 'error',
            //buttonMessage: 'See order',
            callback: () => {},
            //actionType: 'link', // link - close
            //linkTo: '/account/orders/open',
            position: 'topRight'
          })
        )
      }
    )
  }

  return (
    <div className={Styles.formContainer}>
      <h3>KYC Process</h3>

      <div className={Styles.stepNumberContainer}>
        <div
          style={{
            backgroundColor: theme === 'light' ? 'white' : 'black',
            color: theme === 'light' ? 'black' : 'white'
          }}
        >
          3
        </div>
        <div>
          <span style={{ fontSize: 16 }}>Verification Form</span>
        </div>
      </div>
      <div className={Styles.formSectionTitle}>
        <h5>Additional Information</h5>
        <RequiredIndicator />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 35 }}>
        <FormRow>
          <Input
            type='select'
            inputLabel='Industry of Work'
            onChange={(e, value) => {
              setValue('industry', value)
              trigger('industry')
              setIndustry(value)
            }}
            selectValue={industry}
            error={errors.industry}
            placeholder='Select your industry'
            variant='secondary'
            selectOptions={[
              { value: 'finance', label: 'Finance' },
              { value: 'technology', label: 'Technology' },
              { value: 'healthcare', label: 'Healthcare' },
              { value: 'education', label: 'Education' },
              { value: 'retail', label: 'Retail' },
              { value: 'real_estate', label: 'Real Estate' },
              { value: 'manufacturing', label: 'Manufacturing' },
              { value: 'hospitality', label: 'Hospitality' },
              { value: 'government', label: 'Government' },
              { value: 'other', label: 'Other' }
            ]}
            required
          />
          <Input
            type='select'
            inputLabel='Account Purpose'
            onChange={(e, value) => {
              setValue('accountPurpose', value)
              trigger('accountPurpose')
              setAccountPurpose(value)
            }}
            selectValue={accountPurpose}
            error={errors.accountPurpose}
            placeholder='Select your purpose'
            variant='secondary'
            selectOptions={[
              { value: 'savings', label: 'Savings' },
              { value: 'investment', label: 'Investment' },
              { value: 'trading', label: 'Trading' },
              { value: 'hobby', label: 'Hobby' }
            ]}
            required
          />
        </FormRow>

        <FormRow>
          <Input
            type='select'
            inputLabel='Expected Monthly Income'
            onChange={(e, value) => {
              setValue('expectedMonthlyVolume', value)
              trigger('expectedMonthlyVolume')
              setExpectedMonthlyVolume(value)
            }}
            selectValue={expectedMonthlyVolume}
            error={errors.expectedMonthlyVolume}
            placeholder='Select monthly income'
            variant='secondary'
            selectOptions={[
              { value: '0-5000', label: '$0 - $5,000' },
              { value: '5000-10000', label: '$5,000 - $10,000' },
              { value: '10000-20000', label: '$10,000 - $20,000' },
              { value: '20000-50000', label: '$20,000 - $50,000' },
              { value: '50000-above', label: '$50,000 and above' }
            ]}
            required
          />
          <Input
            type='select'
            inputLabel='Source of Funds'
            onChange={(e, value) => {
              setValue('sourceOfFunds', value)
              trigger('sourceOfFunds')
              setSourceOfFunds(value)
            }}
            selectValue={sourceOfFunds}
            error={errors.sourceOfFunds}
            placeholder='Select source of funds'
            variant='secondary'
            selectOptions={[
              { value: 'employment', label: 'Employment' },
              { value: 'pension', label: 'Pension/Social Benefits' },
              { value: 'savings', label: 'Savings' },
              { value: 'investment', label: 'Investments' },
              { value: 'scholarship', label: 'Scholarship' },
              { value: 'inheritance', label: 'Inheritance' },
              { value: 'sale', label: 'Sale/Rental of Real Estate' }
            ]}
            required
          />
        </FormRow>

        <FormRow>
          <Input
            type='select'
            inputLabel='Are you a politically exposed person?'
            onChange={(e, value) => {
              setValue('politicalPerson', value)
              trigger('politicalPerson')
              setPoliticalPerson(value)
            }}
            selectValue={politicalPerson}
            error={errors.politicalPerson}
            placeholder='Select an option'
            variant='secondary'
            selectOptions={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' }
            ]}
            required
          />
          <div></div>
        </FormRow>
        <FormRow>
          <CheckboxInput
            checkboxId='privacy'
            labelFor='privacy'
            label={
              <>
                I have read and agree to the{' '}
                <Link to='/privacy-and-policy' target='_blank' rel='noopener noreferrer'>
                  Privacy and Policy
                </Link>
              </>
            }
            register={{ ...register('privacy') }}
            error={errors.privacy}
            required
          />
        </FormRow>
        <FormRow>
          <CheckboxInput
            checkboxId='terms'
            labelFor='terms'
            label={
              <>
                I have read and agree to the{' '}
                <Link to='/terms-and-conditions' target='_blank' rel='noopener noreferrer'>
                  terms and conditions
                </Link>
              </>
            }
            register={{ ...register('acceptTerms') }}
            error={errors.acceptTerms}
            required
          />
        </FormRow>
        <FormRow>
          <CheckboxInput
            checkboxId='risks'
            labelFor='risks'
            label='I have read & understood the risks related to Cryptocurrency'
            register={{ ...register('risks') }}
            error={errors.risks}
            required
          />
        </FormRow>

        <Button
          text='Proceed'
          disabled={loading || !watch('privacy') || !watch('acceptTerms') || !watch('risks')}
          isLoading={loading}
          style={{ margin: '50px auto 0 auto' }}
          type='submit'
          width='200px'
        />
      </form>
    </div>
  )
}

export default VerificationForm
